import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { FiEdit2 } from 'react-icons/fi';
import { ArrowContainer, Popover, PopoverPosition } from 'react-tiny-popover';
import { ContextualAttachment } from '@/features/ai-agents/shared/models';
import { BigButton } from '@/components/big-button';

// Shared hook for selector popover logic
function useSelectorPopover(initialSelector = ``) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectorInput, setSelectorInput] = useState(initialSelector);

  const openPopover = (selector = ``) => {
    setSelectorInput(selector);
    setIsPopoverOpen(true);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent, onSave: () => void) => {
    if (e.key === `Enter`) {
      e.preventDefault();
      onSave();
    } else if (e.key === `Escape`) {
      closePopover();
    }
  };

  return {
    isPopoverOpen,
    selectorInput,
    setSelectorInput,
    openPopover,
    closePopover,
    handleKeyDown,
  };
}

// Shared popover content component
interface SelectorPopoverContentProps {
  title: string;
  buttonText: string;
  selectorInput: string;
  onSelectorChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSave: () => void;
  position: PopoverPosition;
  childRect: ClientRect;
  popoverRect: ClientRect;
}

function SelectorPopoverContent({
  title,
  buttonText,
  selectorInput,
  onSelectorChange,
  onKeyDown,
  onSave,
  position,
  childRect,
  popoverRect,
}: SelectorPopoverContentProps) {
  return (
    <ArrowContainer
      position={position}
      childRect={childRect}
      popoverRect={popoverRect}
      arrowColor="white"
      arrowSize={8}
    >
      <PopoverContent>
        <PopoverTitle>{title}</PopoverTitle>
        <Divider />
        <SelectorInput
          autoFocus
          value={selectorInput}
          onChange={(e) => onSelectorChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="TYPE CSS SELECTOR"
        />
        <ButtonGroup>
          <BigButton
            fillWidth
            noShadow
            br="0.8rem"
            onClick={onSave}
            disabled={!selectorInput.trim()}
          >
            {buttonText}
          </BigButton>
        </ButtonGroup>
      </PopoverContent>
    </ArrowContainer>
  );
}

export interface ChatContextualTileProps {
  attachment: ContextualAttachment;
  canDelete?: boolean;
  onDelete?: (() => void) | ((id: string) => void);
  onEdit?: (oldAttachment: ContextualAttachment, newSelector: string) => void;
}

export function ChatContextualTile({
  attachment,
  canDelete,
  onDelete,
  onEdit,
}: ChatContextualTileProps) {
  const {
    isPopoverOpen,
    selectorInput,
    setSelectorInput,
    openPopover,
    closePopover,
    handleKeyDown,
  } = useSelectorPopover(attachment.selector);

  const onDeleteClicked = (ev: React.MouseEvent) => {
    if (canDelete && onDelete) {
      ev.stopPropagation();
      if (onDelete.length === 0) {
        (onDelete as () => void)();
      } else {
        (onDelete as (id: string) => void)(attachment.id);
      }
    }
  };

  const onEditClicked = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    openPopover(attachment.selector || ``);
  };

  const onSaveSelector = () => {
    if (onEdit && selectorInput.trim()) {
      onEdit(attachment, selectorInput.trim());
    }
    closePopover();
  };

  return (
    <Popover
      isOpen={isPopoverOpen && attachment.editable === true && !!onEdit}
      onClickOutside={closePopover}
      positions={[`bottom`, `top`, `left`, `right`]}
      containerStyle={{ zIndex: `9999` }}
      content={({ position, childRect, popoverRect }) => (
        <SelectorPopoverContent
          title="Replace an element with this selector"
          buttonText="Update"
          selectorInput={selectorInput}
          onSelectorChange={setSelectorInput}
          onKeyDown={(e) => handleKeyDown(e, onSaveSelector)}
          onSave={onSaveSelector}
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
        />
      )}
    >
      <Wrapper layout>
        <Caption>{attachment.caption}</Caption>
        <ButtonsGroup>
          {attachment.editable && onEdit && (
            <EditButton onClick={onEditClicked} title="Edit selector">
              <FiEdit2 size={12} />
            </EditButton>
          )}
          {canDelete && (
            <DeleteButton onClick={onDeleteClicked}>✕</DeleteButton>
          )}
        </ButtonsGroup>
      </Wrapper>
    </Popover>
  );
}

const Wrapper = styled(motion.div)`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1.2rem;
  border-radius: 0.4rem;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.06);
  border: solid 0.5px #ff8fd0;
  background-color: #ffe3f4;
  color: #e02073;
  font-size: 1.2rem;
  font-weight: 500;
  position: relative;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  font-family: Inter, sans-serif;

  user-select: none;
  && * {
    user-select: none;
  }

  &&::selection {
    background-color: #ff4fa3;
    color: #ffffff;
  }
`;

export const AddContextButton = styled(Wrapper)`
  && {
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 1.4rem;
    height: 3.1rem;
    width: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      background-color: #ffd1e6;
    }

    &:active {
      background-color: #ffb3d1;
    }
  }
`;

export interface AddContextWithSelectorButtonProps {
  onAddWithSelector: (selector: string) => void;
  children?: React.ReactNode;
}

export function AddContextWithSelectorButton({
  onAddWithSelector,
  children,
}: AddContextWithSelectorButtonProps) {
  const {
    isPopoverOpen,
    selectorInput,
    setSelectorInput,
    openPopover,
    closePopover,
    handleKeyDown,
  } = useSelectorPopover();

  const onAddClicked = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    openPopover(``);
  };

  const onSaveSelector = () => {
    if (onAddWithSelector && selectorInput.trim()) {
      onAddWithSelector(selectorInput.trim());
    }
    closePopover();
  };

  return (
    <Popover
      isOpen={isPopoverOpen}
      onClickOutside={closePopover}
      positions={[`bottom`, `top`, `left`, `right`]}
      containerStyle={{ zIndex: `9999` }}
      content={({ position, childRect, popoverRect }) => (
        <SelectorPopoverContent
          title="Add an element with this selector"
          buttonText="Add"
          selectorInput={selectorInput}
          onSelectorChange={setSelectorInput}
          onKeyDown={(e) => handleKeyDown(e, onSaveSelector)}
          onSave={onSaveSelector}
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
        />
      )}
    >
      <div onClick={onAddClicked}>{children}</div>
    </Popover>
  );
}

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #eee;
`;

const Caption = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
`;

const ButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 1.8rem;
  height: 1.8rem;
  cursor: pointer;
  color: rgba(217, 70, 166, 0.6);
  padding: 0;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: rgba(217, 70, 166, 0.1);
    color: rgba(217, 70, 166, 0.8);
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 1.8rem;
  height: 1.8rem;
  cursor: pointer;
  color: rgba(217, 70, 166, 0.46);
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(217, 70, 166, 0.1);
  }
`;

const PopoverContent = styled.div`
  background: white;
  border-radius: 0.8rem;
  padding: 1.6rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PopoverTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: #272d33;
  font-family: Inter, sans-serif;
`;

const SelectorInput = styled.input`
  padding: 0.8rem 1.2rem;
  border: 1px solid #d0d0d0;
  border-radius: 2rem;
  font-size: 1.2rem;
  font-family: 'JetBrains Mono', monospace;
  color: #272d33;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #d0d0d0;
  }

  &::placeholder {
    color: #555;
    font-family: Inter, sans-serif;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: flex-end;
`;
